/**
 * Kiosk form factor constants
 * Defines supported form factors and their dimensions
 */

import { KioskFormFactor } from '../types/kiosk-types';
import { FormFactorDimensions } from '../../core/types/device-config-types';

/**
 * 4-inch form factor configuration
 * Square screen (480x480px)
 */
export const KIOSK_4_INCH: KioskFormFactor = {
  id: '4inch',
  dimensions: {
    width: 480,
    height: 480,
  },
  cardSize: {
    width: 450,
    height: 114,
  },
  maxVisibleCards: 3,
  displayName: '4" Display (480x480)',
};

/**
 * 5-inch form factor configuration
 * Portrait screen (720x1280px)
 */
export const KIOSK_5_INCH: KioskFormFactor = {
  id: '5inch',
  dimensions: {
    width: 720,
    height: 1280,
  },
  cardSize: {
    width: 660,
    height: 200,
  },
  maxVisibleCards: 5,
  displayName: '5" Display (720x1280)',
};

/**
 * All supported kiosk form factors
 */
export const KIOSK_FORM_FACTORS: KioskFormFactor[] = [
  KIOSK_4_INCH,
  KIOSK_5_INCH,
];

/**
 * Map form factor ID to configuration
 */
export const FORM_FACTOR_MAP: Record<string, KioskFormFactor> = {
  '4inch': KIOSK_4_INCH,
  '5inch': KIOSK_5_INCH,
};

/**
 * Get form factor configuration by ID
 *
 * @param formFactorId - Form factor ID
 * @returns Form factor configuration or undefined
 */
export function getKioskFormFactor(formFactorId: string): KioskFormFactor | undefined {
  return FORM_FACTOR_MAP[formFactorId];
}

/**
 * Get form factor dimensions for generic device config
 *
 * @param formFactorId - Form factor ID
 * @returns FormFactorDimensions or undefined
 */
export function getFormFactorDimensions(formFactorId: string): FormFactorDimensions | undefined {
  const formFactor = getKioskFormFactor(formFactorId);
  if (!formFactor) return undefined;

  return {
    width: formFactor.dimensions.width,
    height: formFactor.dimensions.height,
    displayName: formFactor.displayName,
  };
}
